import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Autocomplete,
  Avatar,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { Search, CheckBox, CheckBoxOutlineBlank } from "@mui/icons-material";
import axios from "axios";
import { hostname, parseError, stringToVintageColor } from "../utils";

export interface Collaborator {
  id: string;
  uid: string;
  email: string;
  role?: string;
  displayName?: string;
  photoURL?: string;
}

interface PaginationInfo {
  page: number;
  per_page: number;
  total: number;
  pages: number;
  has_next: boolean;
  has_prev: boolean;
}

interface CommonProps {
  companyId: string;
  getToken: () => Promise<string>;
  onError?: (error: string) => void;
  label?: string;
  helperText?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  variant?: "outlined" | "standard" | "filled";
  size?: "small" | "medium";
  showRole?: boolean;
  sx?: any;
  maxHeight?: number;
}

interface MultiModeProps extends CommonProps {
  multiple?: true;
  // For multi-mode, value holds user IDs (uid).
  value: string[];
  onChange: (userIds: string[], collaborators?: Collaborator[]) => void;
}

interface SingleModeProps extends CommonProps {
  multiple: false;
  // For single-mode, value holds an email (kept for backward compatibility
  // with the original single-select CollaboratorSelect API).
  value: string;
  onChange: (email: string, collaborator?: Collaborator) => void;
}

type MultiCollaboratorSelectProps = MultiModeProps | SingleModeProps;

const DEBOUNCE_MS = 300;
const PER_PAGE = 50;

const MultiCollaboratorSelect: React.FC<MultiCollaboratorSelectProps> = (
  props
) => {
  const {
    companyId,
    getToken,
    onError,
    helperText,
    disabled = false,
    fullWidth = true,
    size = "medium",
    showRole = true,
    sx,
    maxHeight = 300,
  } = props;
  const multiple = props.multiple !== false;
  const label = props.label ?? (multiple ? "Select Team Members" : "Select Collaborator");
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  // Maintain a separate cache for selected collaborators so they persist across searches
  const [selectedCollaboratorCache, setSelectedCollaboratorCache] = useState<
    Map<string, Collaborator>
  >(new Map());
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const listboxRef = useRef<HTMLUListElement | null>(null);
  const lastSearchRef = useRef<string>("");
  // Tracks the most recent reason for an input value change, so the debounced
  // search effect can ignore programmatic resets (e.g. when single-select
  // fills the input with the chosen option's label).
  const lastInputReasonRef = useRef<"input" | "reset" | "clear">("input");

  // Fetch collaborators from backend with search and pagination
  const fetchCollaborators = async (
    search: string = "",
    page: number = 1,
    append: boolean = false
  ) => {
    if (page === 1) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const token = await getToken();
      const response = await axios.get(
        `${hostname}/company_users/${companyId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            page,
            per_page: PER_PAGE,
            search: search.trim(),
          },
        }
      );

      const newCollaborators = response.data.company_users || [];
      const paginationData = response.data.pagination;

      if (append) {
        // Append new results, avoiding duplicates
        setCollaborators((prev) => {
          const existingIds = new Set(prev.map((c) => c.id));
          const uniqueNew = newCollaborators.filter(
            (c: Collaborator) => !existingIds.has(c.id)
          );
          return [...prev, ...uniqueNew];
        });
      } else {
        setCollaborators(newCollaborators);
      }

      setPagination(paginationData);
      lastSearchRef.current = search;
    } catch (error) {
      const errorMessage = parseError(error);
      console.error("Error fetching collaborators:", error);
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Initial load
  useEffect(() => {
    if (companyId) {
      fetchCollaborators("", 1, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // Debounced search - only triggers on inputValue changes
  useEffect(() => {
    // Clear any existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Don't search if input matches what we already searched for
    if (inputValue === lastSearchRef.current) {
      return;
    }

    debounceTimerRef.current = setTimeout(() => {
      fetchCollaborators(inputValue, 1, false);
    }, DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue]);

  // Load more when scrolling to bottom
  const handleScroll = (event: React.UIEvent<HTMLUListElement>) => {
    const target = event.target as HTMLUListElement;
    const isNearBottom =
      target.scrollHeight - target.scrollTop - target.clientHeight < 50;

    if (isNearBottom && pagination?.has_next && !loadingMore && !loading) {
      fetchCollaborators(lastSearchRef.current, pagination.page + 1, true);
    }
  };

  const getDisplayName = useCallback((collaborator: Collaborator) => {
    return collaborator.displayName || collaborator.email;
  }, []);

  const getInitials = useCallback((collaborator: Collaborator) => {
    if (collaborator.displayName) {
      const parts = collaborator.displayName.split(" ");
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return collaborator.displayName[0].toUpperCase();
    }
    return collaborator.email[0].toUpperCase();
  }, []);

  // Normalize the `value` prop into an array of selection keys.
  // - multi-mode: array of uids (as provided).
  // - single-mode: array of 0 or 1 emails. We key the cache by uid in multi
  //   mode and by email in single mode so the same plumbing works for both.
  const selectedKeys = useMemo<string[]>(() => {
    if (multiple) {
      return (props.value as string[]) || [];
    }
    const email = props.value as string;
    return email ? [email] : [];
  }, [multiple, props.value]);

  const keyFor = useCallback(
    (c: Collaborator) => (multiple ? (c.uid as string) : c.email),
    [multiple]
  );

  // Update the selected collaborator cache when collaborators load
  // This ensures we always have the full data for selected users
  useEffect(() => {
    setSelectedCollaboratorCache((prev) => {
      const newCache = new Map(prev);
      collaborators.forEach((c) => {
        const k = keyFor(c);
        if (selectedKeys.includes(k)) {
          newCache.set(k, c);
        }
      });
      return newCache;
    });
  }, [collaborators, selectedKeys, keyFor]);

  // Build selected collaborators from cache, preserving all selections
  const selectedCollaborators = useMemo(() => {
    return selectedKeys
      .map((k) => selectedCollaboratorCache.get(k))
      .filter((c): c is Collaborator => c !== undefined);
  }, [selectedKeys, selectedCollaboratorCache]);

  const emit = useCallback(
    (next: Collaborator[]) => {
      if (multiple) {
        const userIds = next.map((c) => c.uid as string);
        (props.onChange as MultiModeProps["onChange"])(userIds, next);
      } else {
        const first = next[0];
        (props.onChange as SingleModeProps["onChange"])(
          first?.email || "",
          first
        );
      }
    },
    [multiple, props.onChange]
  );

  const handleToggle = useCallback(
    (collaborator: Collaborator) => {
      const k = keyFor(collaborator);
      const isSelected = selectedKeys.includes(k);
      let nextSelected: Collaborator[];
      if (isSelected) {
        if (multiple) {
          nextSelected = selectedCollaborators.filter(
            (c) => keyFor(c) !== k
          );
        } else {
          nextSelected = [];
        }
      } else {
        setSelectedCollaboratorCache((prev) => {
          const newCache = new Map(prev);
          newCache.set(k, collaborator);
          return newCache;
        });
        nextSelected = multiple
          ? [...selectedCollaborators, collaborator]
          : [collaborator];
      }
      emit(nextSelected);
    },
    [multiple, selectedKeys, selectedCollaborators, keyFor, emit]
  );

  // Make sure already-selected collaborators stay in the options list, even
  // when they no longer appear in the current search results — otherwise MUI
  // warns about controlled values not being in the option list.
  const options = useMemo<Collaborator[]>(() => {
    const present = new Set(collaborators.map(keyFor));
    const extras = selectedCollaborators.filter((c) => !present.has(keyFor(c)));
    return extras.length ? [...extras, ...collaborators] : collaborators;
  }, [collaborators, selectedCollaborators, keyFor]);

  return (
    <FormControl fullWidth={fullWidth} sx={sx}>
      <Autocomplete
        multiple={multiple}
        disableCloseOnSelect={multiple}
        disabled={disabled}
        loading={loading || loadingMore}
        options={options}
        value={multiple ? selectedCollaborators : selectedCollaborators[0] || null}
        inputValue={inputValue}
        onInputChange={(_, newInputValue, reason) => {
          if (reason === "input" || reason === "clear") {
            lastInputReasonRef.current = reason;
            setInputValue(newInputValue);
          } else if (reason === "reset" && !multiple) {
            // In single-select mode, MUI fires "reset" after a selection
            // commits so the input can show the chosen label.
            lastInputReasonRef.current = "reset";
            setInputValue(newInputValue);
            // Pretend we already searched for this text so the debounce
            // effect skips firing a search for the option's label.
            lastSearchRef.current = newInputValue;
          }
        }}
        onChange={(_, newValue) => {
          if (multiple) {
            const arr = (newValue as Collaborator[]) || [];
            setSelectedCollaboratorCache((prev) => {
              const newCache = new Map(prev);
              arr.forEach((c) => newCache.set(keyFor(c), c));
              return newCache;
            });
            emit(arr);
          } else {
            const single = newValue as Collaborator | null;
            if (single) {
              setSelectedCollaboratorCache((prev) => {
                const newCache = new Map(prev);
                newCache.set(keyFor(single), single);
                return newCache;
              });
            }
            emit(single ? [single] : []);
          }
        }}
        getOptionLabel={(option) =>
          `${getDisplayName(option)} (${option.email})`
        }
        isOptionEqualToValue={(option, val) => keyFor(option) === keyFor(val)}
        filterOptions={(opts) => opts}
        size={size}
        ListboxProps={{
          ref: listboxRef,
          style: { maxHeight, overflow: "auto" },
          onScroll: handleScroll,
        }}
        PaperComponent={({ children, ...props }) => (
          <Paper {...props} sx={{ maxHeight: maxHeight + 50 }}>
            {children}
            {loadingMore && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  py: 1,
                }}
              >
                <CircularProgress size={20} />
                <Typography variant="caption" sx={{ ml: 1 }}>
                  Loading more...
                </Typography>
              </Box>
            )}
          </Paper>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={
              selectedCollaborators.length === 0
                ? loading
                  ? ""
                  : "Search users..."
                : ""
            }
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <InputAdornment position="start">
                    {loading ? (
                      <CircularProgress size={18} />
                    ) : (
                      <Search fontSize="small" color="action" />
                    )}
                  </InputAdornment>
                  {params.InputProps.startAdornment}
                </>
              ),
            }}
          />
        )}
        renderTags={(tagValue) => {
          const maxVisible = 3;
          const visible = tagValue.slice(0, maxVisible);
          const remaining = tagValue.length - maxVisible;

          return (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {visible.map((collaborator) => (
                <Chip
                  key={collaborator.uid}
                  avatar={
                    <Avatar
                      src={collaborator.photoURL}
                      sx={{
                        width: 24,
                        height: 24,
                        fontSize: "0.75rem",
                        bgcolor: stringToVintageColor(getDisplayName(collaborator)),
                      }}
                    >
                      {getInitials(collaborator)}
                    </Avatar>
                  }
                  label={getDisplayName(collaborator)}
                  size="small"
                  onDelete={() => handleToggle(collaborator)}
                />
              ))}
              {remaining > 0 && (
                <Chip
                  label={`+${remaining} more`}
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
          );
        }}
        renderOption={(props, option, { selected }) => {
          const { key, ...otherProps } = props;
          return (
            <Box
              component="li"
              key={key}
              {...otherProps}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                py: 0.5,
                px: 1,
              }}
              onClick={
                multiple
                  ? (e) => {
                      e.preventDefault();
                      handleToggle(option);
                    }
                  : otherProps.onClick
              }
            >
              {multiple && (
                <Checkbox
                  checked={selected}
                  size="small"
                  icon={<CheckBoxOutlineBlank fontSize="small" />}
                  checkedIcon={<CheckBox fontSize="small" />}
                />
              )}
              <Avatar
                src={option.photoURL}
                sx={{
                  width: 32,
                  height: 32,
                  fontSize: "0.8rem",
                  bgcolor: stringToVintageColor(getDisplayName(option)),
                }}
              >
                {getInitials(option)}
              </Avatar>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap>
                  {getDisplayName(option)}
                </Typography>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  {option.displayName && (
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {option.email}
                    </Typography>
                  )}
                  {showRole && option.role && (
                    <Chip
                      label={option.role}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: "0.65rem",
                        textTransform: "capitalize",
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          );
        }}
      />
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export default MultiCollaboratorSelect;
